import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  type ChartAccount,
  type AccountType,
  type CreateAccountInput,
  ACCOUNT_TYPE_LABELS,
} from "@/hooks/useChartOfAccounts";

interface AccountFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  account?: ChartAccount | null;
  parentAccounts: ChartAccount[];
  onSubmit: (values: CreateAccountInput) => Promise<boolean>;
}

export function AccountFormDialog({
  open,
  onOpenChange,
  mode,
  account,
  parentAccounts,
  onSubmit,
}: AccountFormDialogProps) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [parentId, setParentId] = useState<string>("__none__");
  const [accountType, setAccountType] = useState<AccountType>("fixed_expense");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;

    if (mode === "edit" && account) {
      setCode(account.code);
      setName(account.name);
      setParentId(account.parent_id || "__none__");
      setAccountType(account.account_type);
    } else {
      setCode("");
      setName("");
      setParentId("__none__");
      setAccountType("fixed_expense");
    }
  }, [open, mode, account]);

  const canSubmit = code.trim().length > 0 && name.trim().length > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setIsSubmitting(true);
    try {
      const success = await onSubmit({
        code: code.trim(),
        name: name.trim(),
        parent_id: parentId === "__none__" ? null : parentId,
        account_type: accountType,
      });
      if (success) {
        onOpenChange(false);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter parent accounts to prevent circular references
  const availableParents = parentAccounts.filter((p) => {
    if (mode === "edit" && account) {
      // Can't be parent of itself
      if (p.id === account.id) return false;
      // Can't be child of itself (would need recursive check for full tree)
    }
    return true;
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === "edit" ? "Editar Conta" : "Nova Conta"}
          </DialogTitle>
          <DialogDescription>
            {mode === "edit"
              ? "Atualize os dados da conta do plano de contas."
              : "Cadastre uma nova conta no plano de contas."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label htmlFor="account_code">Código *</Label>
              <Input
                id="account_code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Ex: 4.1.01"
                disabled={mode === "edit" && account?.is_system}
              />
            </div>

            <div className="space-y-2 col-span-2">
              <Label htmlFor="account_name">Nome *</Label>
              <Input
                id="account_name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Salários"
                disabled={mode === "edit" && account?.is_system}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Conta Pai</Label>
            <Select value={parentId} onValueChange={setParentId}>
              <SelectTrigger>
                <SelectValue placeholder="Nenhuma (conta raiz)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Nenhuma (conta raiz)</SelectItem>
                {availableParents.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.code} - {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Tipo de Conta *</Label>
            <Select
              value={accountType}
              onValueChange={(v) => setAccountType(v as AccountType)}
              disabled={mode === "edit" && account?.is_system}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(ACCOUNT_TYPE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {mode === "edit" && account?.is_system && (
            <p className="text-sm text-muted-foreground">
              Esta é uma conta do sistema e não pode ser totalmente editada.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || isSubmitting || (mode === "edit" && account?.is_system)}
          >
            {isSubmitting ? "Salvando..." : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
